import React, { useMemo, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import Select from "react-select";
import moment from "moment";
import { LuAlertCircle, LuBuilding2, LuDatabase } from "react-icons/lu";
import { toast } from "react-toastify";

import CuttingReceiveLines, {
  CuttingReceiveLineView,
} from "./CuttingReceiveLines";
import CuttingReceivePickerModal, {
  CuttingReceiveCardProps,
} from "./CuttingReceivePickerModal";
import {
  CuttingCategoryOption,
  categoryText,
  useCategoryBoxes,
} from "../cuttingLines/CuttingCategoryBoxes";
import Button from "../ui/Button";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardContentClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formCardClass,
  formSelectIconClass,
  gradientSubmitButtonClass,
} from "../ui/styles";
import { useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import {
  closeAfterQty,
  lineName,
  rateSourceLabel,
} from "../../static/cuttingLine";
import { formatAmount } from "../../static/materialLine";
import { rateNumber } from "../../static/processRate";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { CuttingCategoryProps } from "../../types/cuttingOrder";
import {
  CuttingReceiveProps,
  ICuttingReceiveFormInput,
  ICuttingReceiveLineInput,
  OpenCuttingLineProps,
  OpenCuttingOrderProps,
} from "../../types/cuttingReceive";
import { PartyOptionProps } from "../../types/materialLine";

type Props = {
  defaultValues?: ICuttingReceiveFormInput;
  // The receive being edited, with what its lines received: until the party's orders load, its rows show it.
  savedReceive?: CuttingReceiveProps;
  partyOptions: PartyOptionProps[];
  openOrders: OpenCuttingOrderProps[];
  openOrdersLoading: boolean;
  // Called when another party is picked, so the page can load that party's open orders.
  onPartyChange: (partyId: number) => void;
  onSubmit: (data: ICuttingReceiveFormInput) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  submitLabel: string;
  // Rates only for a user who may see them.
  withRates: boolean;
};

// The form of a cutting receive, laid out like the order form: the party, then its pending order lines in category
// boxes, picked from the picker one order line per card, each with the pieces received now and whether that closes the
// line. For a user who may see rates, each line's rate starts at its order line's and can be typed over; the server
// works out the amounts.
const CuttingReceiveForm = ({
  defaultValues,
  savedReceive,
  partyOptions,
  openOrders,
  openOrdersLoading,
  onPartyChange,
  onSubmit,
  isSubmitting,
  onCancel,
  submitLabel,
  withRates,
}: Props) => {
  const sizeUnit = useAppSelector(getSizeUnit);

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<ICuttingReceiveFormInput>({
    defaultValues: defaultValues || {
      party_id: 0,
      date: moment().format("YYYY-MM-DDTHH:mm"),
      items: [],
    },
  });

  const { fields, remove, replace } = useFieldArray({ control, name: "items" });

  const partyId = useWatch({ control, name: "party_id" });
  const watchedItems = useWatch({ control, name: "items" });
  const items = useMemo(() => watchedItems || [], [watchedItems]);

  // The category whose order lines the picker shows, while it is open.
  const [pickerCategoryId, setPickerCategoryId] = useState<number | null>(
    null
  );
  const [showBoxErrors, setShowBoxErrors] = useState(false);

  // The receive's own lines stay listed while it is edited, even once their order line isn't open any more.
  const [keptLineIds] = useState(
    () => new Set((defaultValues?.items || []).map((item) => item.order_item_id))
  );

  // The receive's lines as saved, by order line.
  const savedByLine = useMemo(
    () =>
      new Map(
        (savedReceive?.items || []).map((item) => [item.order_item_id, item])
      ),
    [savedReceive]
  );

  // Every line of the party's orders, with its order.
  const lineById = useMemo(() => {
    const lines = new Map<
      number,
      { line: OpenCuttingLineProps; order: OpenCuttingOrderProps }
    >();

    openOrders.forEach((order) =>
      order.items.forEach((line) => lines.set(line.id, { line, order }))
    );

    return lines;
  }, [openOrders]);

  // An order line the receive can take: one still awaited, one the receive had when the form opened, or one on the form.
  const isListed = useMemo(() => {
    const onForm = new Set(items.map((item) => item.order_item_id));

    return (line: OpenCuttingLineProps) =>
      (line.pending_qty > 0 && !line.closed) ||
      keptLineIds.has(line.id) ||
      onForm.has(line.id);
  }, [items, keptLineIds]);

  // What each line shows: its order line, or until the party's orders load the line as the receive saved it.
  const views = useMemo(
    () =>
      items.map((input: ICuttingReceiveLineInput): CuttingReceiveLineView => {
        const line = lineById.get(input.order_item_id)?.line;
        const saved = savedByLine.get(input.order_item_id);

        // For a user who may see rates: the rate typed on the line against the order line's, which it starts at.
        const orderRate = line?.rate;
        const rate = withRates ? rateNumber(input.rate) ?? undefined : undefined;
        const typedOver =
          rate !== undefined &&
          orderRate !== undefined &&
          Math.abs(rate - orderRate) >= 0.005;

        return {
          key: String(input.order_item_id),
          categoryId:
            (line ? line.item?.category_id : saved?.item?.category_id) ?? 0,
          orderId: line?.order_id ?? saved?.order_item?.order_id ?? null,
          code:
            line?.item?.name ?? saved?.item?.name ?? `#${input.order_item_id}`,
          sizeName: line ? line.size?.name ?? null : saved?.size?.name ?? null,
          imageUrl: line?.item?.image_url ?? saved?.item?.image_url ?? null,
          materialName: line
            ? line.material?.name ?? null
            : saved?.order_item?.material?.name ?? null,
          cutLength: line?.cut_length ?? saved?.order_item?.cut_length ?? null,
          rateHint:
            !withRates || !line || orderRate === undefined
              ? null
              : typedOver
              ? `બદલેલ ભાવ • ઓર્ડર ભાવ ₹${formatAmount(orderRate)}`
              : rate === undefined
              ? `${rateSourceLabel(line.rate_source)} ₹${formatAmount(
                  orderRate
                )}`
              : rateSourceLabel(line.rate_source),
          pending: line ? line.pending_qty : null,
          problem:
            line || openOrdersLoading
              ? null
              : "આ ઓર્ડર ની આઈટમ મળી નથી, તેને કાઢી નાખો",
        };
      }),
    [items, lineById, savedByLine, openOrdersLoading, withRates]
  );

  // A new receive starts with one empty box; a receive being edited with a box per category of its lines, in line order.
  const lineCategoryIds = useMemo(
    () => views.map((view) => view.categoryId),
    [views]
  );
  const {
    boxes,
    addBox,
    removeBox,
    changeBoxCategory,
    resetBoxes,
    boxProblem,
  } = useCategoryBoxes(
    () =>
      (defaultValues?.items || []).map(
        (line) => savedByLine.get(line.order_item_id)?.item?.category_id ?? 0
      ),
    lineCategoryIds,
    remove
  );

  // The picker's cards: one per order line the receive can take, oldest order first.
  const cards = useMemo(() => {
    const list: CuttingReceiveCardProps[] = [];

    openOrders.forEach((order) =>
      order.items.forEach((line) => {
        if (!isListed(line)) return;

        list.push({
          lineId: line.id,
          categoryId: line.item?.category_id ?? 0,
          itemId: line.item_id,
          code: line.item?.name || `#${line.item_id}`,
          sizeName: line.size?.name ?? null,
          subtitle: line.item?.parent
            ? `મુખ્ય આઈટમ: ${line.item.parent.name}`
            : line.item?.description ?? null,
          editItemId: line.item?.parent?.id ?? line.item_id,
          imageUrl: line.item?.image_url ?? null,
          materialName: line.material?.name ?? null,
          cutLength: line.cut_length,
          rate: line.rate,
          rateSource: line.rate_source,
          orderId: order.id,
          orderDate: order.date,
          qty: line.qty,
          received: line.received_qty,
          closed: line.closed,
        });
      })
    );

    return list;
  }, [openOrders, isListed]);

  // Every category the form can name: those of the party's order lines and those of the saved lines.
  const categoryById = useMemo(() => {
    const categories = new Map<number, CuttingCategoryProps>();

    openOrders.forEach((order) =>
      order.items.forEach((line) => {
        if (line.item?.category) {
          categories.set(line.item.category.id, line.item.category);
        }
      })
    );

    (savedReceive?.items || []).forEach((item) => {
      if (item.item?.category && !categories.has(item.item.category.id)) {
        categories.set(item.item.category.id, item.item.category);
      }
    });

    return categories;
  }, [openOrders, savedReceive]);

  // The select offers the categories the receive can take order lines in.
  const categoryOptions = useMemo((): CuttingCategoryOption[] => {
    const offered = new Map<number, CuttingCategoryProps>();

    openOrders.forEach((order) =>
      order.items.forEach((line) => {
        if (line.item?.category && isListed(line)) {
          offered.set(line.item.category.id, line.item.category);
        }
      })
    );

    return Array.from(offered.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((category) => ({ value: category.id, label: categoryText(category) }));
  }, [openOrders, isListed]);

  const categoryLabel = (categoryId: number | null) => {
    const category =
      categoryId !== null ? categoryById.get(categoryId) : undefined;

    return category ? categoryText(category) : null;
  };

  const handlePickerClose = (lines: ICuttingReceiveLineInput[]) => {
    setPickerCategoryId(null);

    const current = getValues("items");
    const same =
      lines.length === current.length &&
      lines.every(
        (line, index) =>
          line.order_item_id === current[index].order_item_id &&
          line.qty === current[index].qty &&
          line.rate === current[index].rate &&
          line.close_order === current[index].close_order
      );

    if (!same) {
      replace(lines);
      clearErrors("items");
    }
  };

  // A row's qty that comes to make up its order line ticks "ઓર્ડર બંધ", and one that no longer does unticks it, as on the
  // picker's cards.
  const handleQtyChange = (index: number, previous: string, qty: string) => {
    const line = lineById.get(getValues(`items.${index}.order_item_id`))?.line;

    if (!line) return;

    const closeOrder = getValues(`items.${index}.close_order`);
    const next = closeAfterQty(
      line.qty,
      line.received_qty,
      previous,
      qty,
      closeOrder
    );

    if (next !== closeOrder) setValue(`items.${index}.close_order`, next);
  };

  const canPick = !!partyId && !openOrdersLoading;
  const pickerLabel = !partyId
    ? "પહેલા પાર્ટી પસંદ કરો"
    : openOrdersLoading
    ? "આઈટમ લોડ થઈ રહી છે..."
    : "આઈટમ પસંદ કરો";

  const handleOnSubmit: SubmitHandler<ICuttingReceiveFormInput> = (data) => {
    // Every box needs a category and an item of it; there is always a box, so a receive without lines stops here.
    const boxError = boxProblem();

    if (boxError) {
      toast.error(boxError, { ...(toastStyles as any) });
      return;
    }

    const problem = views.find((view) => view.problem);

    if (problem) {
      toast.error(
        `${lineName(problem.code, problem.sizeName)}: ${problem.problem}`,
        { ...(toastStyles as any) }
      );
      return;
    }

    // A line is checked against its order line, which has to be loaded.
    if (openOrdersLoading) {
      toast.error("ઓર્ડર ની વિગતો લોડ થઈ રહી છે, થોડી વાર પછી સાચવો", {
        ...(toastStyles as any),
      });
      return;
    }

    onSubmit(data);
  };

  return (
    <div className={formCardClass}>
      <div className={cx(cardContentClass, "pt-6")}>
        <form
          onSubmit={(event) => {
            setShowBoxErrors(true);
            handleSubmit(handleOnSubmit)(event);
          }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="cutting-receive-party" className={fieldLabelClass}>
                પાર્ટી પસંદ કરો <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.party_id && "error")}>
                <LuBuilding2 className={formSelectIconClass} />
                <Controller
                  control={control}
                  name="party_id"
                  rules={{ required: true, min: 1 }}
                  render={({ field }) => (
                    <Select
                      inputId="cutting-receive-party"
                      ref={field.ref}
                      options={partyOptions}
                      value={
                        partyOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(option: any) => {
                        const value = option?.value || 0;

                        if (value === field.value) return;

                        field.onChange(value);

                        // Another party's orders: the lines and boxes so far belong to the old party's orders.
                        if (items.length > 0) {
                          replace([]);
                          clearErrors("items");
                          toast.info(
                            "પાર્ટી બદલતાં પસંદ કરેલ આઈટમ કાઢી નાખી છે",
                            { ...(toastStyles as any) }
                          );
                        }
                        resetBoxes();
                        setShowBoxErrors(false);

                        onPartyChange(value);
                      }}
                      placeholder="પાર્ટી સિલેક્ટ કરો..."
                      noOptionsMessage={() =>
                        "બાકી કટીંગ ઓર્ડર વાળી પાર્ટી મળી નથી"
                      }
                      styles={iconSelectStyles}
                      isSearchable
                      className={cx(
                        "react-select-container",
                        errors.party_id && "error"
                      )}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              {errors.party_id && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  પાર્ટી સિલેક્ટ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="cutting-receive-date" className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.date && "error")}>
                <LuDatabase className={formSelectIconClass} />
                <Input
                  id="cutting-receive-date"
                  type="datetime-local"
                  className={fieldInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.date && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  તારીખ અને સમય દાખલ કરો
                </p>
              )}
            </div>
          </div>

          <CuttingReceiveLines
            control={control}
            fields={fields}
            errors={errors}
            views={views}
            boxes={boxes}
            categoryOptions={categoryOptions}
            categoryLabel={categoryLabel}
            categoriesMessage={
              !partyId
                ? "પહેલા પાર્ટી પસંદ કરો"
                : openOrdersLoading
                ? "કેટેગરી લોડ થઈ રહી છે..."
                : "આ પાર્ટી ની કોઈ બાકી આઈટમ નથી"
            }
            showBoxErrors={showBoxErrors}
            onAddBox={addBox}
            onRemoveBox={removeBox}
            onChangeBoxCategory={changeBoxCategory}
            onRemove={remove}
            onOpenPicker={setPickerCategoryId}
            onChangeQty={handleQtyChange}
            canPick={canPick}
            pickerLabel={pickerLabel}
            withRates={withRates}
            sizeUnit={sizeUnit}
          />

          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 pt-4">
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                રદ કરો
              </Button>

              <button
                type="submit"
                className={gradientSubmitButtonClass}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className={buttonSpinnerClass}></div>
                    સાચવી રહ્યા છીએ...
                  </div>
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {pickerCategoryId !== null && (
        <CuttingReceivePickerModal
          cards={cards.filter((card) => card.categoryId === pickerCategoryId)}
          cardsLoading={openOrdersLoading}
          categoryLabel={categoryLabel(pickerCategoryId) ?? undefined}
          initialLines={getValues("items")}
          withRates={withRates}
          sizeUnit={sizeUnit}
          onClose={handlePickerClose}
        />
      )}
    </div>
  );
};

export default CuttingReceiveForm;
